import { useMemo, useState, type ComponentType, type FormEvent, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconEdit, IconPlus } from '@tabler/icons-react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CenteredLoader } from '../../components/LoadingSpinner';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { useConfirm } from '../../shared/ConfirmContext';
import { useCurrency } from '../../shared/CurrencyContext';
import { usePermissions } from '../../shared/permissions';
import CategoryFilterBoard from './components/CategoryFilterBoard';
import InfiniteScrollBox from './components/InfiniteScrollBox';
import PriorityReorderTable from './components/PriorityReorderTable';
import PriorityToggleButton from './components/PriorityToggleButton';
import TableSearchBar from './components/TableSearchBar';
import WarehouseCreateModal from './components/WarehouseCreateModal';
import { useCategoryFilterOptions } from './hooks/useCategoryFilterOptions';
import { useDebouncedSearch } from './hooks/useDebouncedSearch';
import { usePriorityReorder } from './hooks/usePriorityReorder';
import { useSortOrdering } from './hooks/useSortOrdering';

interface Warehouse {
  id: number;
  name: string;
  location: string;
  description: string;
  supplier?: number | string | null;
}

interface WarehouseFormData {
  name: string;
  location: string;
  description: string;
  supplier: number | string | null;
}

interface StockItem {
  id: number;
  product: number;
  product_name: string;
  product_sku: string;
  product_category_name: string;
  product_type_name?: string | null;
  default_daily_rate_display?: string | number | null;
  tracking_type: string;
  quantity_owned: number;
  quantity_available: number;
  priority: number | null;
  effective_priority: number | null;
}

interface StockItemsPage {
  results?: StockItem[];
  items?: StockItem[];
  next?: string | null;
}

interface PriorityReorderProps {
  items: StockItem[];
  droppableId: string;
  onDragEnd: (result: unknown) => Promise<void>;
  saving: boolean;
  hint: string;
  columns: Array<{
    key: string;
    label: string;
    className?: string;
    headerClassName?: string;
    render: (stockItem: StockItem) => ReactNode;
  }>;
}

interface InfiniteScrollProps {
  children: ReactNode;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
}

interface PriorityReorderOptions {
  priorityField: string;
  fetchSortItems: () => Promise<StockItem[]>;
  saveOrder: (items: StockItem[]) => Promise<unknown>;
  onExit: () => void;
}

interface PriorityReorderResult {
  sortMode: boolean;
  savingOrder: boolean;
  sortItems: StockItem[];
  enterSortMode: () => Promise<void>;
  exitSortMode: () => void;
  handleDragEnd: (result: unknown) => Promise<void>;
}

const TypedPriorityReorderTable = PriorityReorderTable as unknown as ComponentType<PriorityReorderProps>;
const TypedInfiniteScrollBox = InfiniteScrollBox as unknown as ComponentType<InfiniteScrollProps>;
const useTypedPriorityReorder = usePriorityReorder as unknown as (options: PriorityReorderOptions) => PriorityReorderResult;

const asList = (response: StockItemsPage | StockItem[] | undefined): StockItem[] => {
  if (Array.isArray(response)) return response;
  return response?.results || response?.items || [];
};

export default function StockInventory() {
  const { warehouseId } = useParams<{ warehouseId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const { hasPerm } = usePermissions();
  const { searchInput, search, handleSearchInputChange, handleSearchSubmit } = useDebouncedSearch();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<WarehouseFormData>({ name: '', location: '', description: '', supplier: '' });
  const { ordering, toggleOrder, getSortClass } = useSortOrdering('priority_order');

  const { data: warehouse, isLoading: warehouseLoading } = useQuery<Warehouse>({
    queryKey: ['warehouse', warehouseId],
    queryFn: () => api.get<Warehouse>(`warehouses/api/warehouses/${warehouseId}/`),
    enabled: Boolean(warehouseId),
  });
  const categories = useCategoryFilterOptions(['stock-categories', warehouseId], `warehouses/api/stock-items/categories_all/?stock=${warehouseId}`);

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get('warehouses/api/suppliers/?ordering=name'),
  });

  const { data, isLoading: itemsLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<StockItemsPage>({
    queryKey: ['stock-inventory', warehouseId, search, selectedCategory, ordering],
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams({ stock: warehouseId || '', page: String(pageParam), ordering });
      if (search.trim()) params.set('search', search.trim());
      if (selectedCategory !== 'all') params.set('product__category', selectedCategory);
      return api.getRaw(`warehouses/api/stock-items/?${params}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next ? new URL(lastPage.next).searchParams.get('page') : undefined,
    enabled: Boolean(warehouseId),
  });
  const stockItems = useMemo(() => data?.pages.flatMap(asList) || [], [data]);
  const loading = warehouseLoading || itemsLoading;

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.stockitem' || e.model === 'warehouses.product') {
      queryClient.invalidateQueries({ queryKey: ['stock-inventory', warehouseId] });
    }
  });

  const updateWarehouse = useMutation({
    mutationFn: (formData: WarehouseFormData) => api.patch<Warehouse>(`warehouses/api/warehouses/${warehouseId}/`, {
      ...formData,
      supplier: formData.supplier || null,
    }),
    onSuccess: (updatedWarehouse) => {
      queryClient.setQueryData(['warehouse', warehouseId], updatedWarehouse);
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      setShowEditModal(false);
    },
  });

  const openEditModal = () => {
    if (!warehouse) return;
    setEditForm({
      name: warehouse.name,
      location: warehouse.location,
      description: warehouse.description,
      supplier: warehouse.supplier ?? '',
    });
    updateWarehouse.reset();
    setShowEditModal(true);
  };

  const handleEditSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateWarehouse.mutate(editForm);
  };

  const handleDuplicate = async (stockItem: StockItem) => {
    try {
      const duplicate = await api.post<{ id: number }>(`warehouses/api/products/${stockItem.product}/duplicate/`, {});
      queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate(`/warehouses/products/${duplicate.id}?edit=1`);
    } catch (error) {
      console.error('Product duplication failed:', error);
    }
  };

  const handleDelete = async (stockItem: StockItem) => {
    if (!await confirm(t('warehouses.inventory.deleteConfirm', { name: stockItem.product_name }))) return;
    try {
      await api.delete(`warehouses/api/stock-items/${stockItem.id}/`);
      queryClient.invalidateQueries({ queryKey: ['stock-inventory', warehouseId] });
      queryClient.invalidateQueries({ queryKey: ['stock-categories', warehouseId] });
    } catch (error) {
      console.error('Stock item deletion failed:', error);
    }
  };

  const { sortMode, savingOrder, sortItems, enterSortMode, exitSortMode, handleDragEnd } = useTypedPriorityReorder({
    priorityField: 'effective_priority',
    fetchSortItems: async () => {
      const params = new URLSearchParams({ stock: warehouseId || '', page_size: '500', ordering: 'priority_order' });
      if (search.trim()) params.set('search', search.trim());
      if (selectedCategory !== 'all') params.set('product__category', selectedCategory);
      return asList(await api.get<StockItemsPage>(`warehouses/api/stock-items/?${params}`));
    },
    saveOrder: (withUpdatedPriorities: StockItem[]) => api.post('warehouses/api/stock-items/bulk_update_priority/', {
      stock_item_priorities: withUpdatedPriorities.map((stockItem) => ({ id: stockItem.id, priority: stockItem.priority })),
    }),
    onExit: () => queryClient.invalidateQueries({ queryKey: ['stock-inventory'] }),
  });

  return <PageShell
    pretitle={t('warehouses.inventory.pretitle')}
    title={warehouse?.name || t('warehouses.inventory.fallbackTitle')}
    description={warehouse?.location || t('warehouses.inventory.fallbackDescription')}
    actions={<div className="d-flex flex-wrap gap-2">
      <Link to="/warehouses/stocks" className="btn btn-outline-secondary"><IconArrowLeft size={16} className="me-1" />{t('warehouses.inventory.back')}</Link>
      {hasPerm('warehouses.change_stock') && <Button variant="outline-primary" onClick={openEditModal} disabled={!warehouse}><IconEdit size={16} className="me-1" />{t('warehouses.inventory.edit')}</Button>}
      <Link to={`/warehouses/${warehouseId}/items/add`} className="btn btn-primary"><IconPlus size={16} className="me-1" />{t('warehouses.inventory.addProduct')}</Link>
    </div>}
  >
    <PageSection>
      <div className="d-flex flex-column flex-md-row mb-3 gap-2">
        <TableSearchBar value={searchInput} onChange={handleSearchInputChange} onSubmit={handleSearchSubmit} placeholder={t('warehouses.inventory.searchPlaceholder')} submitLabel={t('warehouses.inventory.search')} />
        {hasPerm('warehouses.view_productcategory') && (
          <Link to="/warehouses/products/categories" className="btn btn-outline-primary">
            {t('warehouses.categories.title')}
          </Link>
        )}
        {hasPerm('warehouses.view_producttype') && (
          <Link to="/warehouses/products/types" className="btn btn-outline-primary">
            {t('warehouses.types.title')}
          </Link>
        )}
        <PriorityToggleButton active={sortMode} saving={savingOrder} onClick={sortMode ? exitSortMode : enterSortMode} activeLabel={t('warehouses.inventory.done')} inactiveLabel={t('warehouses.inventory.changePriority')} />
      </div>

      <CategoryFilterBoard categories={categories} selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} allLabel={t('warehouses.inventory.allCategories')} />

      {loading ? <CenteredLoader /> : sortMode ? (
        <TypedPriorityReorderTable
          items={sortItems}
          droppableId="stock-items-sort"
          onDragEnd={handleDragEnd}
          saving={savingOrder}
          hint={t('warehouses.inventory.reorderHint')}
          columns={[
            { key: 'product_name', label: t('warehouses.inventory.product'), className: 'fw-semibold', render: (stockItem: StockItem) => stockItem.product_name },
            { key: 'product_category_name', label: t('warehouses.inventory.category'), render: (stockItem: StockItem) => stockItem.product_category_name },
            { key: 'product_type_name', label: t('warehouses.inventory.type'), render: (stockItem: StockItem) => stockItem.product_type_name || stockItem.tracking_type || '-' },
            { key: 'quantity_owned', label: t('warehouses.inventory.owned'), className: 'text-end', headerClassName: 'text-end', render: (stockItem: StockItem) => stockItem.tracking_type === 'CONSUMABLE' ? <span className="text-muted" title={t('warehouses.products.unlimitedStock')}>&#8734;</span> : stockItem.quantity_owned },
            { key: 'default_daily_rate_display', label: t('warehouses.items.dayTariffAmount'), className: 'text-end', headerClassName: 'text-end', render: (stockItem: StockItem) => formatCurrency(stockItem.default_daily_rate_display || 0) },
          ]}
        />
      ) : <TypedInfiniteScrollBox hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage}>
        <Table hover className="table-vcenter mb-0"><thead style={{ position: 'sticky', top: 0, background: 'var(--tblr-bg-surface, white)', zIndex: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}><tr>
          <th><div className={getSortClass('priority_order')} onClick={() => toggleOrder('priority_order')}>{t('warehouses.inventory.priority')}</div></th>
          <th><div className={getSortClass('product__name')} onClick={() => toggleOrder('product__name')}>{t('warehouses.inventory.product')}</div></th>
          <th><div className={getSortClass('product__category__name')} onClick={() => toggleOrder('product__category__name')}>{t('warehouses.inventory.category')}</div></th>
          <th><div className={getSortClass('product__product_type__name')} onClick={() => toggleOrder('product__product_type__name')}>{t('warehouses.inventory.type')}</div></th>
          <th><div className={getSortClass('quantity_owned')} onClick={() => toggleOrder('quantity_owned')}>{t('warehouses.inventory.owned')}</div></th>
          <th><div className={getSortClass('product__default_tariff__amount')} onClick={() => toggleOrder('product__default_tariff__amount')}>{t('warehouses.items.dayTariffAmount')}</div></th>
          <th>{t('warehouses.inventory.actions')}</th>
        </tr></thead><tbody>{stockItems.map((stockItem) => <tr key={stockItem.id}>
          <td className="text-muted" style={{ fontSize: '0.8em' }}>{stockItem.effective_priority ?? '-'}</td>
          <td>{stockItem.product_name}</td>
          <td>{stockItem.product_category_name || '-'}</td>
          <td>{stockItem.product_type_name || stockItem.tracking_type || '-'}</td>
          <td>{stockItem.tracking_type === 'CONSUMABLE' ? <span className="text-muted" title={t('warehouses.products.unlimitedStock')}>&#8734;</span> : stockItem.quantity_owned}</td>
          <td>{formatCurrency(stockItem.default_daily_rate_display || 0)}</td>
          <td><div className="btn-group">
            {hasPerm('warehouses.view_product') && <Link to={`/warehouses/products/${stockItem.product}`} className="btn btn-sm btn-outline-primary" title={t('common.view')}><i className="ti ti-eye" /></Link>}
            {hasPerm('warehouses.change_product') && <Link to={`/warehouses/products/${stockItem.product}?edit=1`} className="btn btn-sm btn-outline-info" title={t('common.edit')}><i className="ti ti-edit" /></Link>}
            {hasPerm('warehouses.change_product') && <button type="button" className="btn btn-sm btn-outline-warning" title={t('warehouses.products.duplicate')} onClick={() => handleDuplicate(stockItem)}><i className="ti ti-copy" /></button>}
            {hasPerm('warehouses.delete_stockitem') && <button type="button" className="btn btn-sm btn-outline-danger" title={t('common.delete')} onClick={() => handleDelete(stockItem)}><i className="ti ti-trash" /></button>}
          </div></td>
        </tr>)}</tbody></Table>
        {stockItems.length === 0 && <div className="text-center text-secondary py-4">{t('warehouses.inventory.noResults')}</div>}
      </TypedInfiniteScrollBox>}
    </PageSection>

    {updateWarehouse.isError && <Alert variant="danger" className="app-inline-alert mt-3">{t('warehouses.inventory.saveError')}</Alert>}
    <WarehouseCreateModal
      show={showEditModal}
      mode="edit"
      formData={editForm}
      submitting={updateWarehouse.isPending}
      suppliers={suppliers}
      onClose={() => setShowEditModal(false)}
      onSubmit={handleEditSubmit}
      onFieldChange={(field, value) => setEditForm((current) => ({ ...current, [field]: value }))}
    />
  </PageShell>;
}
